// Who this queue would assign to, right now — read from the router's own query.
//
// The whole point is that a lead can look at this and see the answer production
// would give, rather than inferring it from a schedule grid. When it is empty, the
// per-member breakdown says which of the four gates said no, because "nobody is on
// shift" and "this queue is not assignable" and "Entra took their capability away"
// all produce the identical silence in the sweep log:
// `[routing:scheduler] routing.sweep ok (Nms)`, every five minutes, forever,
// whether that sweep assigned fifty tickets or none.
//
// EVERY SENTENCE COMES FROM ../../pages/admin/on-shift.js, which is pure and tested
// (test/web/on-shift.test.js). Nothing here decides what a member state means —
// there is no DOM harness in this repository, so a ternary written inline is a
// ternary nobody can hold to being total, and a member state that fell through it
// would render a blank cell. A blank cell beside a name reads as "fine", which is
// the same silence one level up.
//
// COLOUR CARRIES NOTHING HERE, on purpose. Eligible and not-eligible are separated
// by the sentence and by ink weight, not by a fill: `--color-sunken` is the row
// hover fill and is byte-identical to slate-100, and every additional grey in this
// app has collided with it at least once. Both themes come out of semantic tokens
// with no `dark:` variant.

import { useEffect, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from '../../pages/admin/descriptor-form.js';
import { memberStatus, eligibleSummary } from '../../pages/admin/on-shift.js';

export default function OnShiftPreview({ queueId, at }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError(null);
    const suffix = at ? `?at=${encodeURIComponent(at)}` : '';
    apiFetch(`/admin/queues/${encodeURIComponent(queueId)}/on-shift${suffix}`,
      { signal: controller.signal })
      .then((body) => { setData(body.data); setError(null); })
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [queueId, at]);

  if (error) {
    return (
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-ink">Who would be assigned right now</h2>
        <div className="mt-3 rounded-lg border border-line-strong bg-sunken p-3 text-sm text-ink">
          {error}
        </div>
      </section>
    );
  }
  if (!data) return <p className="mt-8 text-sm text-ink-muted">Loading the on-shift preview…</p>;

  const summary = eligibleSummary(data.eligible);
  const members = data.members ?? [];

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-ink">
        {at ? 'Who would be assigned then' : 'Who would be assigned right now'}
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        Computed from the same query the round-robin runs, not from a copy of it.
      </p>

      <p className={`mt-2 text-sm ${summary.empty ? 'text-ink' : 'text-ink font-medium'}`}>
        {summary.text}
      </p>

      {members.length === 0 ? (
        <p className="mt-3 text-sm text-ink-muted">
          This queue has no members, so there is nobody to be on shift.
        </p>
      ) : (
        <ul className="mt-3 divide-y divide-line rounded-lg border border-line bg-surface">
          {members.map((member) => {
            const status = memberStatus(member);
            return (
              // Stacked below `sm`, name-and-reason on one line above it.
              //
              // MEASURED, and not the failure I first assumed. Neither layout
              // overflows — `scrollWidth === clientWidth === 492` in headless Edge
              // both before and after, because `truncate` on the name absorbs all
              // the pressure. That IS the bug: the reasons run to ~60 characters
              // and `shrink-0` made them win, so a narrow row rendered
              // "D. | Their Entra role cannot own tickets" — the name, the only
              // part a lead is looking for, truncated to two letters to protect
              // the sentence explaining it. Stacking gives both their full width.
              <li
                key={member.id}
                className="flex flex-col gap-0.5 px-3 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
              >
                <span
                  className={`min-w-0 truncate text-sm ${status.eligible ? 'text-ink' : 'text-ink-muted'}`}
                  title={member.email ?? ''}
                >
                  {member.display_name}
                </span>
                <span className="text-xs text-ink-secondary sm:shrink-0 sm:text-right">
                  {status.text}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Said here rather than discovered in December. `schedule_exceptions` was cut
          from the plan, so there is no holiday table and no one-off override
          anywhere in this system — and this preview agrees with production about
          that, which is the point of it agreeing with production at all. */}
      <p className="mt-3 text-xs text-ink-muted">
        Holidays are not modelled. There is no exception or time-off table anywhere in this
        system, so an agent on leave whose weekly shift covers this moment is on shift here
        and will be assigned tickets. Remove them from the queue to stop that.
      </p>
    </section>
  );
}
